"use client";

import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Send } from "lucide-react";
import { listMessages, sendMessage, resolveApprovalRequest } from "@/actions/chat";
import type { ChatMessage, ChatUser, ThreadType } from "@/types/chat";

interface ChatPanelProps {
  threadType: ThreadType;
  threadId: string;
  currentUser: { id: string; username: string } | null;
  users: ChatUser[];
  className?: string;
  hideHeader?: boolean;
  currentUserRole?: string;
}

export default function ChatPanel({
  threadType,
  threadId,
  currentUser,
  users,
  className,
  hideHeader,
  currentUserRole,
}: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [isPending, startTransition] = useTransition();
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyInput, setReplyInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const loadMessages = useCallback(() => {
    startTransition(async () => {
      const msgs = await listMessages(threadType, threadId);
      setMessages(msgs);
    });
  }, [threadType, threadId]);

  // Initial load
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Poll for new messages
  useEffect(() => {
    pollRef.current = setInterval(loadMessages, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [threadType, threadId, loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Parse @mentions from content
  function extractMentions(text: string): string[] {
    const mentionRegex = /@(\w+)/g;
    const found: string[] = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const username = match[1];
      const user = users.find(
        (u) => u.username.toLowerCase() === username.toLowerCase(),
      );
      if (user) found.push(user.id);
    }
    return [...new Set(found)];
  }

  async function handleSend() {
    if (!input.trim() || !currentUser) return;

    const mentions = extractMentions(input);
    const payload = {
      thread_type: threadType,
      thread_id: threadId,
      author_id: currentUser.id,
      author_name: currentUser.username,
      content: input.trim(),
      mentions,
    };

    setInput("");
    setShowMentions(false);

    startTransition(async () => {
      const msg = await sendMessage(payload);
      if (msg) {
        setMessages((prev) => [...prev, msg]);
      }
    });
  }

  async function handleSendApproval() {
    if (!input.trim() || !currentUser) return;

    const officeUserIds = users
      .filter((u) => u.id !== currentUser.id && u.role !== "service-tech")
      .map((u) => u.id);

    const payload = {
      thread_type: threadType,
      thread_id: threadId,
      author_id: currentUser.id,
      author_name: currentUser.username,
      content: input.trim(),
      mentions: [] as string[],
      message_type: "approval_request" as const,
      notify_user_ids: officeUserIds,
    };

    setInput("");
    setShowMentions(false);

    startTransition(async () => {
      const msg = await sendMessage(payload);
      if (msg) {
        setMessages((prev) => [...prev, msg]);
      }
    });
  }

  async function handleResolve(messageId: string) {
    if (!replyInput.trim() || !currentUser) return;

    const replyPayload = {
      thread_type: threadType,
      thread_id: threadId,
      author_id: currentUser.id,
      author_name: currentUser.username,
      content: replyInput.trim(),
      mentions: [] as string[],
    };

    const capturedReply = replyInput.trim();
    setReplyInput("");
    setReplyingToId(null);

    startTransition(async () => {
      const [replyMsg] = await Promise.all([
        sendMessage(replyPayload),
        resolveApprovalRequest(messageId, currentUser.id, currentUser.username),
      ]);
      setMessages((prev) =>
        prev
          .map((m) =>
            m.id === messageId ? { ...m, approval_status: "resolved" as const } : m,
          )
          .concat(replyMsg ? [replyMsg] : []),
      );
      void capturedReply;
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    setInput(val);

    // Detect @ trigger
    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);

    if (atMatch) {
      setShowMentions(true);
      setMentionFilter(atMatch[1].toLowerCase());
    } else {
      setShowMentions(false);
      setMentionFilter("");
    }
  }

  function insertMention(user: ChatUser) {
    const cursor = inputRef.current?.selectionStart ?? input.length;
    const textBefore = input.slice(0, cursor);
    const textAfter = input.slice(cursor);
    const atIdx = textBefore.lastIndexOf("@");
    if (atIdx === -1) return;

    const newText = textBefore.slice(0, atIdx) + `@${user.username} ` + textAfter;
    setInput(newText);
    setShowMentions(false);
    inputRef.current?.focus();
  }

  const filteredUsers = users.filter(
    (u) =>
      u.id !== currentUser?.id &&
      (u.username.toLowerCase().includes(mentionFilter) ||
        (u.name?.toLowerCase().includes(mentionFilter) ?? false)),
  );

  function renderContent(text: string) {
    // Highlight @mentions
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        const username = part.slice(1);
        const isValid = users.some(
          (u) => u.username.toLowerCase() === username.toLowerCase(),
        );
        return (
          <span
            key={i}
            className={cn(
              "font-semibold",
              isValid ? "text-blue-600 dark:text-blue-400" : "",
            )}
          >
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  }

  if (!currentUser) {
    return (
      <div className={cn("border rounded-lg p-4 text-sm text-muted-foreground", className)}>
        Войдите, чтобы использовать чат.
      </div>
    );
  }

  return (
    <div className={cn("border rounded-lg flex flex-col", className)} data-slot="chat-panel">
      {/* Header */}
      {!hideHeader && (
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">Чат</h3>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0">
        <div ref={scrollRef} className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
          {messages.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-8">
              Нет сообщений. Начните общение!
            </p>
          )}
          {messages.map((msg) => {
            const isMe = msg.author_id === currentUser.id;
            const time = new Date(msg.created_at).toLocaleTimeString("ru-RU", {
              hour: "2-digit",
              minute: "2-digit",
            });

            // ── Approval request message ──────────────────────────────────
            if (msg.message_type === "approval_request") {
              const isApprovalPending = msg.approval_status === "pending";
              const canReply =
                currentUserRole !== "service-tech" && isApprovalPending;
              return (
                <div key={msg.id} className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {msg.author_name}
                    </span>
                    <span className="text-[10px] text-muted-foreground/60">{time}</span>
                  </div>
                  <div
                    className={cn(
                      "rounded-lg border-2 p-3 text-sm max-w-[90%]",
                      isApprovalPending
                        ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20"
                        : "border-green-400 bg-green-50 dark:bg-green-950/20",
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className="size-3.5 shrink-0 text-amber-600" />
                      <span className="text-xs font-semibold">Запрос согласования</span>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] ml-auto shrink-0",
                          isApprovalPending
                            ? "text-amber-600 border-amber-400"
                            : "text-green-600 border-green-400",
                        )}
                      >
                        {isApprovalPending ? "Ожидает" : "✓ Согласовано"}
                      </Badge>
                    </div>
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    {canReply && replyingToId !== msg.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 text-xs"
                        onClick={() => setReplyingToId(msg.id)}
                      >
                        Ответить
                      </Button>
                    )}
                    {replyingToId === msg.id && (
                      <div className="mt-2 flex flex-col gap-2">
                        <textarea
                          value={replyInput}
                          onChange={(e) => setReplyInput(e.target.value)}
                          placeholder="Введите ответ..."
                          rows={2}
                          className="w-full resize-none rounded border bg-background px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleResolve(msg.id);
                            }
                          }}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleResolve(msg.id)}
                            disabled={!replyInput.trim() || isPending}
                          >
                            Отправить ответ
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => {
                              setReplyingToId(null);
                              setReplyInput("");
                            }}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            // ── Regular message ───────────────────────────────────────────
            return (
              <div
                key={msg.id}
                className={cn("flex flex-col gap-0.5", isMe ? "items-end" : "items-start")}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {msg.author_name}
                  </span>
                  <span className="text-[10px] text-muted-foreground/60">
                    {time}
                  </span>
                </div>
                <div
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap break-words",
                    isMe
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                >
                  {renderContent(msg.content)}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Mention dropdown */}
      {showMentions && filteredUsers.length > 0 && (
        <div className="mx-4 mb-1 border rounded-md bg-popover shadow-md max-h-32 overflow-y-auto">
          {filteredUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(u);
              }}
            >
              <span className="font-medium">@{u.username}</span>
              {u.name && (
                <span className="text-muted-foreground ml-2">{u.name}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t flex gap-2 items-end">
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Сообщение... (@ для упоминания)"
          rows={1}
          className="flex-1 resize-none rounded-md border bg-transparent px-3 py-2 text-base md:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[36px] max-h-[120px] field-sizing-content"
        />
        {currentUserRole === "service-tech" && (
          <Button
            size="icon-sm"
            variant="outline"
            onClick={handleSendApproval}
            disabled={!input.trim() || isPending}
            title="Запросить согласование"
            className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 shrink-0"
          >
            <Bell className="size-4" />
          </Button>
        )}
        <Button
          size="icon-sm"
          onClick={handleSend}
          disabled={!input.trim() || isPending}
        >
          <Send className="size-4" />
        </Button>
      </div>
    </div>
  );
}
